/**
 * Keeps a numbered slideshow pager short, the way page numbers do it:
 *
 *     01 — 02 … 05 — 06 — 07 … 12
 *
 * The first, the last, and a window around the current slide stay; everything
 * else collapses behind an ellipsis. With eight slides the full row already
 * ran past the edge of a phone, and it grows with every slide added.
 *
 * Why script rather than CSS: the row is rendered complete and server-side,
 * and which number is current only becomes true in the browser, as
 * aria-selected moves from one button to the next. So this watches for that
 * attribute changing and re-windows the row.
 *
 * Nothing is removed from the DOM -- hidden numbers keep their place in the
 * list and their click targets, they are just not painted -- so the
 * slideshow's own indexing is untouched.
 */
(() => {
  const NUMBERS = '.slideshow-controls__numbers';
  const DIVIDER = 'slideshow-controls__numbers-divider';
  const HIDDEN = 'slideshow-controls__numbers-item--collapsed';
  const ELLIPSIS = 'slideshow-controls__numbers-ellipsis';

  /** How many numbers either side of the current one stay visible. */
  const NEIGHBOURS = 1;

  const isDivider = (li) => li.classList.contains(DIVIDER);
  const isEllipsis = (li) => li.classList.contains(ELLIPSIS);

  function makeEllipsis() {
    const li = document.createElement('li');
    li.className = ELLIPSIS;
    li.setAttribute('aria-hidden', 'true');
    li.textContent = '…';
    return li;
  }

  function update(list) {
    const children = [...list.children].filter((li) => !isEllipsis(li));
    const numbers = children.filter((li) => !isDivider(li));
    const total = numbers.length;

    // Short rows need none of this.
    if (total <= 5) {
      list.querySelectorAll('.' + ELLIPSIS).forEach((el) => el.remove());
      numbers.forEach((li) => li.classList.remove(HIDDEN));
      children.filter(isDivider).forEach((li) => li.classList.remove(HIDDEN));
      return;
    }

    let current = numbers.findIndex((li) => li.querySelector('[aria-selected="true"]'));
    if (current < 0) current = 0;

    const keep = new Set([0, total - 1, current]);
    for (let d = 1; d <= NEIGHBOURS; d++) {
      keep.add(current - d);
      keep.add(current + d);
    }

    numbers.forEach((li, i) => li.classList.toggle(HIDDEN, !keep.has(i)));

    // A divider is only worth drawing between two numbers that are both shown.
    children.forEach((li, i) => {
      if (!isDivider(li)) return;
      const before = children[i - 1];
      const after = children[i + 1];
      const shown = (el) => el && !el.classList.contains(HIDDEN);
      li.classList.toggle(HIDDEN, !(shown(before) && shown(after)));
    });

    // One ellipsis per gap, placed where the gap starts.
    list.querySelectorAll('.' + ELLIPSIS).forEach((el) => el.remove());
    for (let i = 1; i < total; i++) {
      if (keep.has(i) && !keep.has(i - 1)) {
        numbers[i].before(makeEllipsis());
      }
    }
  }

  const lists = new WeakSet();

  function watch(list) {
    if (lists.has(list)) return;
    lists.add(list);
    update(list);
    // aria-selected moves between buttons as the slideshow advances.
    new MutationObserver(() => update(list)).observe(list, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });
  }

  const scan = (root) => (root || document).querySelectorAll(NUMBERS).forEach(watch);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  // The section can be re-rendered (theme editor, section rendering), which
  // replaces the row with a fresh, complete one.
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.addedNodes.length) {
        scan();
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
